import '@react-pdf-viewer/core/lib/styles/index.css';

import { Viewer, Worker } from '@react-pdf-viewer/core';
import { ChevronRight } from 'lucide-react';
import { FC, Fragment, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Error, Modal, Spinner } from 'src/components';
import { getLocalizedLangValue } from 'src/helpers';
import { useGetRegulations, useQueryHelper } from 'src/queries';
import { useLanguageState, useOrganizationState } from 'src/state';
import { RegulationFirestoreResponse } from 'src/types';

import { RegulationButton, RegulationContainer, RegulationTitle, ScrollContainer, Title } from './Regulations.styled';

export const Regulations: FC = () => {
  const { t } = useTranslation();
  const { organizationData } = useOrganizationState();
  const regulationsCollections = useGetRegulations(organizationData.hotel);
  const { data: regulations, refetch } = regulationsCollections;
  const { isLoading, isError } = useQueryHelper({ queries: [regulationsCollections] });
  const [pdfModal, setPdfModal] = useState(false);
  const [activeRegulation, setActiveRegulation] = useState<RegulationFirestoreResponse | null>(null);
  const { language: currentLang } = useLanguageState();

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return <Error info errorTitle="Brak dostępnych regulaminów" action={refetch} />;
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Regulamin</title>
        <meta name="description" content="Hotelspot - Regulamin" />
      </Helmet>
      <ScrollContainer>
        <Title>{t('menu_items.regulations')}</Title>
        <div className="w-full grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-2">
          {regulations?.length && regulations?.map((regulation: RegulationFirestoreResponse) => (
            <RegulationContainer
              onClick={() => {
                setActiveRegulation(regulation);
                setPdfModal(true);
              }}
              key={regulation.id}
            >
              <RegulationTitle>{getLocalizedLangValue(regulation.name, currentLang)}</RegulationTitle>
              <RegulationButton>
                <ChevronRight size={22} color="#1C1C1C" />
              </RegulationButton>
            </RegulationContainer>
          ))}
        </div>
      </ScrollContainer>
      <Modal
        size="full"
        isOpen={pdfModal}
        onClose={() => {
          setActiveRegulation(null);
          setPdfModal(false);
        }}
        title={activeRegulation ? getLocalizedLangValue(activeRegulation.name, currentLang) : ''}
      >
        <Fragment key="modal">
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
            <Viewer
              fileUrl={activeRegulation ? getLocalizedLangValue(activeRegulation.file, currentLang) : ''}
              defaultScale={1}
            />
          </Worker>
        </Fragment>
      </Modal>
    </>
  );
};
